import React from "react";
import { UseMutateFunction } from "@tanstack/react-query";

import { MutationError } from "@Types/index";
import useCurrentUser from "@Hooks/useCurrentUser";
import { LenderTutorial } from "@Types/lender_tutorials";
import { LenderBlogPost } from "@Types/lender_blogposts";

import styles from "./LikeButton.module.scss";

interface LikeButtonProps {
  entity: LenderBlogPost | LenderTutorial;
  className?: string;
  useLike: (id: string) => {
    like: UseMutateFunction<
      LenderBlogPost | LenderTutorial,
      MutationError,
      {
        id: string;
      },
      unknown
    >;
    loading: boolean;
    success: boolean;
    error: MutationError;
  };
}

const LikeButton: React.FC<LikeButtonProps> = ({
  entity,
  className = "",
  useLike,
}) => {
  const user = useCurrentUser();

  const { like, loading: isLoading } = useLike(entity._id);

  const likes = entity.data.likes;
  const likesCount = likes?.count;
  const isLiked = likes?.userIds?.includes(user?._id as string);
  const handleClick = async () => {
    like({ id: entity._id });
  };

  return (
    <button
      className={`${styles.likeButton} ${isLiked ? styles.liked : ""} ${
        isLoading ? styles.loading : ""
      } ${className}`}
      onClick={handleClick}
      disabled={isLoading}
      aria-label={isLiked ? "Unlike post" : "Like post"}
      title={isLiked ? "Unlike post" : "Like post"}
    >
      {!isLiked && "Like"}
      <span className={styles.icon}>
        <svg
          width="20"
          height="18"
          viewBox="0 0 20 18"
          fill={isLiked ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M17.984 2.016a5.102 5.102 0 0 0-7.215 0L10 2.785l-.769-.769a5.102 5.102 0 1 0-7.215 7.215l7.215 7.215.769.769.769-.769 7.215-7.215a5.102 5.102 0 0 0 0-7.215z" />
        </svg>
      </span>
      <span className={styles.count}>{likesCount}</span>
      {isLoading && <span className={styles.loadingDot}></span>}
    </button>
  );
};

export default LikeButton;
